import { Spin, Typography } from 'antd';
import { NoDataFound } from 'common/components';
import { useAntdToken } from 'common/hooks/useAntdToken';
import { useDateFormatter } from 'common/hooks/useDateFormatter';
import useInfiniteScrollForMonitoring from 'common/hooks/useInfiniteScrollForMonitoring';
import CommonMonitoringImageModal from 'modules/Monitoring/MonitoringALLModal/ImageModal';
import { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export type MonitoringActivityType =
  | 'image'
  | 'email'
  | 'social'
  | 'chat'
  | 'meeting'
  | 'console';

interface MonitoringEvent {
  trackingId: number;
  employeeId: string;
  trackingType: string;
  timestamp: string | number;
  image_event: string;
  deviceName: string;
  emp_name: string;
  image_content_type: string;
  image_filename?: string;
  project_name?: string;
  task_name?: string;
}

interface MonitoringSnapshotGridProps {
  employeeId?: string;
  type: MonitoringActivityType;
  urlObjKey?: string;
  isOCR?: boolean;
  includeProjectFilters?: boolean;
  title?: string;
  enabled?: boolean;
  maxHeight?: number | string;
  cardWidth?: number;
}

const CalendarIcon = ({ color }: { color: string }) => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.33366 0.916504V2.74984M3.66699 0.916504V2.74984" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.625 5.50016C9.625 3.77168 9.625 2.90744 9.08802 2.37047C8.55108 1.8335 7.6868 1.8335 5.95833 1.8335H5.04167C3.31318 1.8335 2.44894 1.8335 1.91197 2.37047C1.375 2.90744 1.375 3.77168 1.375 5.50016V6.41683C1.375 8.1453 1.375 9.00958 1.91197 9.54651C2.44894 10.0835 3.31318 10.0835 5.04167 10.0835" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.375 4.5835H9.625" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.37209 8.57117L7.79134 8.24984V7.45545M9.62467 8.24984C9.62467 9.26234 8.80385 10.0832 7.79134 10.0832C6.77884 10.0832 5.95801 9.26234 5.95801 8.24984C5.95801 7.23733 6.77884 6.4165 7.79134 6.4165C8.80385 6.4165 9.62467 7.23733 9.62467 8.24984Z" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = ({ color }: { color: string }) => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.49935 10.0832C8.03065 10.0832 10.0827 8.03114 10.0827 5.49984C10.0827 2.96853 8.03065 0.916504 5.49935 0.916504C2.96804 0.916504 0.916016 2.96853 0.916016 5.49984C0.916016 8.03114 2.96804 10.0832 5.49935 10.0832Z" stroke={color} strokeWidth="0.7" />
    <path d="M5.5 3.6665V5.49984L6.41667 6.4165" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProgressIcon = ({ color }: { color: string }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.00042 3C6.52702 3 7.04437 3.13862 7.50042 3.40193C7.95647 3.66523 8.33517 4.04394 8.59852 4.5C8.86182 4.95606 9.00042 5.4734 9.00042 6C9.00042 6.5266 8.86182 7.04395 8.59852 7.5C8.33517 7.95605 7.95647 8.33475 7.50042 8.5981C7.04437 8.8614 6.52702 9 6.00042 9C5.47382 9 4.95647 8.8614 4.50042 8.5981C4.04436 8.33475 3.66565 7.95605 3.40234 7.5L6.00042 6V3Z" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TaskIcon = ({ color }: { color: string }) => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.64519 0.916992H4.35352C3.97382 0.916992 3.66602 1.2248 3.66602 1.60449C3.66602 1.98418 3.97382 2.29199 4.35352 2.29199H6.64519C7.02487 2.29199 7.33269 1.98418 7.33269 1.60449C7.33269 1.2248 7.02487 0.916992 6.64519 0.916992Z" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.33204 1.60449C8.04406 1.62595 8.4687 1.70536 8.76263 1.99928C9.16537 2.40201 9.16537 3.05018 9.16537 4.34653V7.3334C9.16537 8.62979 9.16537 9.27797 8.76263 9.68071C8.35989 10.0834 7.71172 10.0834 6.41537 10.0834H4.58203C3.28567 10.0834 2.63749 10.0834 2.23476 9.68071C1.83204 9.27797 1.83204 8.62979 1.83203 7.33344L1.83204 4.34655C1.83204 3.05019 1.83204 2.40201 2.23476 1.99928C2.52868 1.70535 2.95335 1.62595 3.66532 1.60449" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.43555 5.04201L3.89388 5.50034L4.81055 4.35449" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.1875 7.79199H7.5625M6.1875 5.04199H7.5625" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.95312 7.79134H3.89583M4.01042 7.79134C4.01042 7.85459 3.95912 7.90592 3.89583 7.90592C3.83255 7.90592 3.78125 7.85459 3.78125 7.79134C3.78125 7.72805 3.83255 7.67676 3.89583 7.67676C3.95912 7.67676 4.01042 7.72805 4.01042 7.79134Z" stroke={color} strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface SnapshotCardProps {
  image: MonitoringEvent;
  onClick: () => void;
  token: ReturnType<typeof useAntdToken>;
  formatDate: (timestamp: number) => string;
}

const SnapshotCard = ({ image, onClick, token, formatDate }: SnapshotCardProps) => {
  const time = new Date(image.timestamp as number)
    .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
    .toUpperCase();

  const mutedText = token?.colorTextSecondary || '#64748B';
  const iconMuted = token?.customSvgColorForScreenShootCard || '#94A3B8';
  const bodyText = token?.colorText || '#334155';
  const borderColor = token?.customBorderColorTwo || '#D1D5DB';
  const cardBg = token?.customBgColorForScreenShootCard || '#FFFFFF';

  return (
    <div
      onClick={onClick}
      style={{
        width: '100%',
        padding: 6,
        borderRadius: 6,
        border: `1px solid ${borderColor}`,
        backgroundColor: cardBg,
      }}
      className="cursor-pointer hover:shadow-md transition"
    >
      <img
        src={`data:${image.image_content_type};base64,${image.image_event}`}
        alt="Screenshot"
        style={{ width: '100%', height: 139, borderRadius: 5, objectFit: 'cover', display: 'block' }}
      />
      <div className="mt-1 flex flex-col gap-[2px] px-1 pb-1">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <CalendarIcon color={iconMuted} />
            <span style={{ color: mutedText, fontSize: 10, fontWeight: 500, lineHeight: '140%' }} className="truncate">
              {formatDate(image.timestamp as number)}
            </span>
          </div>
          <div className="flex items-center gap-1" style={{ width: 113 }}>
            <ClockIcon color={iconMuted} />
            <span style={{ color: mutedText, fontSize: 10, fontWeight: 500, lineHeight: '140%' }} className="truncate">
              {time}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <ProgressIcon color={bodyText} />
            <span style={{ color: bodyText, fontSize: 12, fontWeight: 500, lineHeight: '140%' }} className="truncate">
              {image.project_name || image.deviceName || '-'}
            </span>
          </div>
          <div className="flex items-center gap-1 min-w-0" style={{ width: 113 }}>
            <TaskIcon color="#1B73E8" />
            <span style={{ color: '#1B73E8', fontSize: 10, fontWeight: 500 }} className="truncate">
              {image.task_name || image.trackingType || '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MonitoringSnapshotGrid = ({
  employeeId,
  type,
  urlObjKey = 'GetImagesWithBlob',
  isOCR = true,
  includeProjectFilters = false,
  title,
  enabled = true,
  maxHeight = '70vh',
  cardWidth = 260,
}: MonitoringSnapshotGridProps) => {
  const token = useAntdToken();
  const { handleDateFormatting } = useDateFormatter();

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px 0px 200px 0px',
  });

  const { data, hasMoreData, isLoading } = useInfiniteScrollForMonitoring({
    employeeId,
    urlObjKey,
    inView,
    type,
    enabled,
    // isOCR,
    // includeProjectFilters,
  });

  const events: MonitoringEvent[] = data?.events || [];

  const [modalVisible, setModalVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);

  const thumbnailsToShow = useMemo(() => {
    if (currentImageIndex === null) return [];
    return events.slice(
      Math.max(currentImageIndex - 2, 0),
      Math.min(currentImageIndex + 3, events.length)
    );
  }, [currentImageIndex, events]);

  const borderColor = token?.customBorderColorTwo || '#D1D5DB';

  return (
    <div className="bg-white rounded-lg" style={{ border: `1px solid ${borderColor}` }}>
      <style>{`
        .monitoring-snapshot-grid-scroll::-webkit-scrollbar { width: 6px; }
        .monitoring-snapshot-grid-scroll::-webkit-scrollbar-track { background: transparent; }
        .monitoring-snapshot-grid-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
        .monitoring-snapshot-grid-scroll::-webkit-scrollbar-thumb:hover { background: #9CA3AF; }
      `}</style>

      {title && (
        <div className="px-4 pt-4 pb-2">
          <Typography.Text strong style={{ fontSize: 16, color: token?.colorText }}>
            {title}
          </Typography.Text>
        </div>
      )}

      <div
        className="monitoring-snapshot-grid-scroll p-4"
        style={{ maxHeight, overflowY: 'auto', overflowX: 'hidden' }}
      >
        {isLoading && events.length === 0 ? (
          <div className="flex items-center justify-center py-10 w-full">
            <Spin />
          </div>
        ) : events.length === 0 ? (
          <div className="min-h-[300px] grid place-content-center">
            <NoDataFound />
          </div>
        ) : (
          <>
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardWidth}px, 1fr))` }}
            >
              {events.map((image, idx) => (
                <SnapshotCard
                  key={`${image.trackingId}-${idx}`}
                  image={image}
                  token={token}
                  formatDate={handleDateFormatting}
                  onClick={() => {
                    setCurrentImageIndex(idx);
                    setModalVisible(true);
                  }}
                />
              ))}
            </div>

            {hasMoreData && (
              <div ref={loadMoreRef} className="w-full flex justify-center py-4">
                <Spin size="small" />
              </div>
            )}
          </>
        )}
      </div>

      <CommonMonitoringImageModal
        modalVisible={modalVisible}
        currentImageIndex={currentImageIndex}
        employeeData={events as any}
        setModalVisible={setModalVisible}
        setCurrentImageIndex={setCurrentImageIndex}
        thumbnailsToShow={thumbnailsToShow as any}
        handleThumbnailClick={(idx: number) => setCurrentImageIndex(idx)}
        goToPrevPage={() =>
          setCurrentImageIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))
        }
        goToNextPage={() =>
          setCurrentImageIndex((prev) =>
            prev !== null && prev < events.length - 1 ? prev + 1 : prev
          )
        }
      />
    </div>
  );
};

export default MonitoringSnapshotGrid;
